import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  DatePicker,
  Button,
  Label,
  MultiSelect,
} from "@/components/ui";
import {
  useFetchFinanceCategoriesQuery,
  useFetchPatientsNoPaginateQuery,
  useFetchProceduresQuery,
  useToast,
} from "@/hooks";
import { DisplayError, formatDateToBackend } from "@/utils";

import { SheetFilterContainer } from "../../sheets";

type FormFilterFinanceEntriesProps = {
  children: React.ReactNode;
};

type HandleFilterFinanceEntriesProps = {
  categoryId: string;
  patientId: string;
  procedureId: string;
  startDate: string;
  endDate: string;
};

export function FormFilterFinanceEntries({
  children,
}: FormFilterFinanceEntriesProps) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const { data: categoriesData, isLoading: isLoadingCategories } =
    useFetchFinanceCategoriesQuery({});
  const { data: patientsData, isLoading: patientsLoading } =
    useFetchPatientsNoPaginateQuery();
  const { data: proceduresData, isLoading: proceduresLoading } =
    useFetchProceduresQuery({ perPage: "100" });

  const initialValues: HandleFilterFinanceEntriesProps = {
    patientId: "",
    procedureId: "",
    categoryId: "",
    startDate: "",
    endDate: "",
  };

  const {
    handleSubmit,
    getFieldProps,
    errors,
    touched,
    isSubmitting,
    resetForm,
    ...formik
  } = useFormik<HandleFilterFinanceEntriesProps>({
    initialValues,
    validationSchema: Yup.object().shape({
      patientId: Yup.string().optional(),
      procedureId: Yup.string().optional(),
      categoryId: Yup.string().optional(),
      startDate: Yup.string().optional(),
      endDate: Yup.string().optional(),
    }),

    onSubmit: async (data) => {
      await handleFilterFinanceEntries(data);
    },
  });

  async function handleFilterFinanceEntries(
    values: HandleFilterFinanceEntriesProps
  ) {
    try {
      if (values.patientId) queryParams.set("patientId", values.patientId);
      if (values.procedureId)
        queryParams.set("procedureId", values.procedureId);
      if (values.categoryId) queryParams.set("categoryId", values.categoryId);
      if (values.startDate)
        queryParams.set("startDate", formatDateToBackend(values.startDate));
      if (values.endDate)
        queryParams.set("endDate", formatDateToBackend(values.endDate));
      await navigate({ search: queryParams.toString() });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error),
        variant: "destructive",
      });
    }
  }

  function clearFilter() {
    queryParams.delete("patientId");
    queryParams.delete("procedureId");
    queryParams.delete("categoryId");
    queryParams.delete("startDate");
    queryParams.delete("endDate");

    navigate({ search: queryParams.toString() });
    resetForm();
  }
  return (
    <SheetFilterContainer button={children}>
      <div className="h-full overflow-y-auto pb-5 px-1">
        <form className="space-y-3" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label>Data inicial</Label>
            <DatePicker
              {...getFieldProps("startDate")}
              isInvalid={touched.startDate && !!errors.startDate}
              isValid={touched.startDate && !errors.startDate}
              messageError={errors.startDate}
            />
          </div>
          <div className="space-y-2">
            <Label>Data final</Label>
            <DatePicker
              {...getFieldProps("endDate")}
              isInvalid={touched.endDate && !!errors.endDate}
              isValid={touched.endDate && !errors.endDate}
              messageError={errors.endDate}
            />
          </div>

          <div className="space-y-2">
            <Label>Selecione o paciente</Label>
            <MultiSelect
              options={
                patientsData?.map((patient) => ({
                  value: patient.id,
                  label: patient.person.name,
                })) || []
              }
              loading={patientsLoading}
              placeholder="Selecione um paciente..."
              isInvalid={touched.patientId && errors.patientId}
              isValid={touched.patientId && !errors.patientId}
              value={formik.values.patientId}
              onChange={(value) => formik.setFieldValue("patientId", value)}
              onBlur={() => formik.handleBlur("patientId")}
              messageError={errors.patientId}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Selecione o procedimento</Label>
            <MultiSelect
              options={
                proceduresData?.items.map((procedure) => ({
                  value: procedure.id,
                  label: procedure.name,
                })) || []
              }
              loading={proceduresLoading}
              isInvalid={touched.procedureId && errors.procedureId}
              isValid={touched.procedureId && !errors.procedureId}
              placeholder="Selecione um procedimento..."
              emptyMessage="Nenhum procedimento encontrado"
              value={formik.values.procedureId}
              onChange={(value) => formik.setFieldValue("procedureId", value)}
              onBlur={() => formik.handleBlur("procedureId")}
              messageError={errors.procedureId}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Selecione a categoria</Label>
            <MultiSelect
              options={
                categoriesData?.map((category) => ({
                  value: category.id,
                  label: category.name,
                })) || []
              }
              loading={isLoadingCategories}
              isInvalid={touched.categoryId && errors.categoryId}
              isValid={touched.categoryId && !errors.categoryId}
              placeholder="Selecione uma categoria..."
              emptyMessage="Nenhuma categoria encontrada"
              value={formik.values.categoryId}
              onChange={(value) => formik.setFieldValue("categoryId", value)}
              onBlur={() => formik.handleBlur("categoryId")}
              messageError={errors.categoryId}
            />
          </div>

          <div className="flex justify-start gap-2">
            <Button type="submit" disabled={isSubmitting}>
              Filtrar
            </Button>
            <Button variant="secondary" type="button" onClick={clearFilter}>
              Limpar filtro
            </Button>
          </div>
        </form>
      </div>
    </SheetFilterContainer>
  );
}
